/**
 * Action URL Resolution
 *
 * Resolves Sanity action objects to link properties that can be used
 * by LinkWrapper or ActionLink components.
 */

import type { Locale } from "@/i18n/config";
import type { ActionToType } from "@/lib/types";
import {
  getLocalizedSlug,
  type LocalizedSlug,
} from "@/lib/routing/url-builder";
import type { ActionLinkProps } from "./types";

/**
 * Build query string from action params array
 */
function buildQueryString(
  params:
    | Array<{ key?: string | null; value?: string | null }>
    | null
    | undefined,
): string {
  if (!params || params.length === 0) return "";

  const queryParams = new URLSearchParams();
  params.forEach((p) => {
    if (p.key && p.value) {
      queryParams.append(p.key, p.value);
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : "";
}

/**
 * Resolve a slug that may be a simple string or a localized slug object.
 */
function resolveSlug(
  slug: string | LocalizedSlug | null | undefined,
  locale: Locale,
): string | null {
  if (!slug) return null;
  if (typeof slug === "string") return slug;
  return getLocalizedSlug(slug, locale);
}

/**
 * Resolve an action to link properties
 *
 * Handles three types of actions:
 * - internal: Links to internal pages (uses next-intl Link for locale handling)
 * - external: Links to external URLs (opens in new tab)
 * - media: Document downloads
 *
 * @param actionTo - The action target from Sanity
 * @param locale - The current locale for resolving localized slugs
 * @returns ActionLinkProps for rendering, or null if invalid
 *
 * @example
 * ```tsx
 * const locale = useLocale() as Locale;
 * const linkProps = getActionUrl(action.to, locale);
 * if (linkProps) {
 *   return <LinkWrapper linkProps={linkProps}>{children}</LinkWrapper>;
 * }
 * ```
 */
export function getActionUrl(
  actionTo: ActionToType,
  locale: Locale,
): ActionLinkProps | null {
  // Internal link - builds locale-aware URL
  if (actionTo._type === "internal" && actionTo.link) {
    const slug = resolveSlug(
      actionTo.link.slug as string | LocalizedSlug | null,
      locale,
    );

    if (process.env.NODE_ENV !== "production" && !slug) {
      // Fail loud in dev, but only for actual failures — anchor-only links
      // (no document referenced, anchor set) are a supported authoring
      // pattern and legitimately have no slug. A referenced document that
      // still yields no slug means it's unpublished or the projection didn't
      // resolve `document->slug` (VAW-1552); no anchor either means the link
      // resolves to nothing at all.
      const unresolvedRef = actionTo.link.hasDocument === true;
      if (unresolvedRef || !actionTo.link.anchor) {
        console.warn(
          "[getActionUrl] Internal link resolved to no slug; falling back to anchor/empty href.",
          actionTo.link,
        );
      }
    }

    let href = "";

    if (slug) {
      href = slug.startsWith("/") ? slug : `/${slug}`;
    }

    // Add query params before fragment
    href += buildQueryString(actionTo.link.params);

    // Add anchor/fragment last
    if (actionTo.link.anchor) {
      href += `#${actionTo.link.anchor}`;
    }

    if (!href) return null;

    return {
      href,
      isExternal: false,
      isDocument: false,
    };
  }

  // External URL - opens in new tab
  if (actionTo._type === "external" && actionTo.link?.url) {
    return {
      href: actionTo.link.url,
      target: "_blank",
      rel: "noopener noreferrer",
      isExternal: true,
      isDocument: false,
    };
  }

  // Media/document download
  if (actionTo._type === "media" && actionTo.link?.file?.asset?.url) {
    return {
      href: actionTo.link.file.asset.url,
      download: "", // Empty string triggers download with server-provided filename
      isExternal: false,
      isDocument: true,
    };
  }

  return null;
}
